import * as React from 'react'
import { useInRouterContext, useSearchParams } from 'react-router-dom'

import createContext from 'modules/core/hooks/createContext'
import { useCallback } from 'modules/core/hooks/useCallback'

import {
  AI_SEARCH_PARAM_KEYS,
  hasAiSearchParams,
} from 'modules/shift_management/lib/aiSearchParams'
import type { FiltersDispatch } from './reducers/filter'
import FilterReducer, { FilterState, initialFiltersState } from './reducers/filter'

type Context = [FilterState, FiltersDispatch]

const { context: FilterContext, hook: useFilters } = createContext<Context>()

function parseEmployeeIds(param: string | null): string[] | null {
  if (!param) return null
  const ids = param
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
  return ids.length > 0 ? [...new Set(ids)] : null
}

function initialFiltersStateFromSearchParams(searchParams: URLSearchParams): FilterState {
  const employeeIdsParam = searchParams.get(AI_SEARCH_PARAM_KEYS.employees)
  const locationIdParam = searchParams.get(AI_SEARCH_PARAM_KEYS.location)
  const teamIdParam = searchParams.get(AI_SEARCH_PARAM_KEYS.team)

  if (!hasAiSearchParams(searchParams)) return initialFiltersState()

  return {
    ...initialFiltersState(),
    employeeIds: parseEmployeeIds(employeeIdsParam),
    locationIds: locationIdParam ? [locationIdParam] : [],
    teamIds: teamIdParam ? [teamIdParam] : null,
    appliedFromAi: true,
  }
}

/**
 * Inner provider that uses useSearchParams — only rendered when inside a Router context.
 * This avoids crashing tests that render ShiftManagementProviders without a Router.
 */
const FiltersProviderWithRouter = ({ children }: React.PropsWithChildren<object>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const memoizedReducer = useCallback(FilterReducer, [])
  const [state, dispatch] = React.useReducer(
    memoizedReducer,
    searchParams,
    initialFiltersStateFromSearchParams
  )

  const isMountRef = React.useRef(true)

  React.useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false

      if (hasAiSearchParams(searchParams)) {
        const next = new URLSearchParams(searchParams)
        Object.values(AI_SEARCH_PARAM_KEYS).forEach((key) => next.delete(key))
        setSearchParams(next, { replace: true })
      }
      return
    }

    const employeeIdsParam = searchParams.get(AI_SEARCH_PARAM_KEYS.employees)
    const locationIdParam = searchParams.get(AI_SEARCH_PARAM_KEYS.location)
    const teamIdParam = searchParams.get(AI_SEARCH_PARAM_KEYS.team)

    if (!hasAiSearchParams(searchParams)) return

    dispatch({
      type: 'FILTER_BY_AI',
      employeeIds: parseEmployeeIds(employeeIdsParam),
      locationIds: locationIdParam ? [locationIdParam] : null,
      teamIds: teamIdParam ? [teamIdParam] : null,
    })

    const next = new URLSearchParams(searchParams)
    Object.values(AI_SEARCH_PARAM_KEYS).forEach((key) => next.delete(key))
    setSearchParams(next, { replace: true })
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  return <FilterContext.Provider value={[state, dispatch]}>{children}</FilterContext.Provider>
}

const FiltersProviderWithoutRouter = ({ children }: React.PropsWithChildren<object>) => {
  const memoizedReducer = useCallback(FilterReducer, [])
  const [state, dispatch] = React.useReducer(memoizedReducer, initialFiltersState())

  return <FilterContext.Provider value={[state, dispatch]}>{children}</FilterContext.Provider>
}

const FiltersProvider = ({ children }: React.PropsWithChildren<object>) => {
  const inRouterContext = useInRouterContext()

  return inRouterContext ? (
    <FiltersProviderWithRouter>{children}</FiltersProviderWithRouter>
  ) : (
    <FiltersProviderWithoutRouter>{children}</FiltersProviderWithoutRouter>
  )
}

export { FiltersProvider, useFilters }
